/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { environment } from '@environments/environment';
import { map } from 'rxjs/operators';
import { OutputAdapterService } from './../output-adapter/output-adapter.service';
import { InputAdapterService } from './../input-adapter/input-adapter.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import {
  ApiParams,
  ChecklistDTO,
  SurveyUpdateDTO,
  PositionDTO,
  WizardDTO,
  SurveyDTO,
  FormDTO,
  DomainListDTO,
  IdentificationStartDTO,
  FormUpdateDTO,
  LabelListDTO,
  UserDataDTO,
  WizardStepNextOutputDTO,
  ReportRequestDTO,
  AuthenticateDTO,
  IdentificationVerifyDTO,
  QuotationDTO,
  CustomerDTO,
  QuotationParamsDTO,
  BankAccountLinkDTO,
  BankAccountEvaluationDTO,
  NoteDTO,
  UserCustomerDTO,
  UserCustomerUpdateDTO,
  AtcDTO,
  PositionSaveRequestDTO,
  PositionRequestDTO,
  NotificationDTO,
  CreditRequestDTO,
  QuotationBoundariesDTO,
  CreditUseListDTO,
  PostsellingCreateDTO,
  TaskListDTO,
  PositionCreateDTO,
  ReportDTO,
  HandoverStartDTO,
  HandoverStatusDTO,
  LogRequestDTO,
  ChannelDTO,
  IdentificationStartRequestDTO,
  CardDTO,
  ResumeInspectDTO,
  ResumeContactsCheckDTO,
  FinalizeDTO,
  OcrCheckDTO,
  OcrScanDTO,
  Api,
  DigitalSignStartDTO,
  DigitalSignVerifyDTO,
  IBMClientRequestDTO,
  IBMClientDTO,
  IBMLoginRequestDTO,
  IBMLoginDTO,
  LoginOtpSendRequestDTO,
  LoginOtpVerifyRequestDTO,
  RegisterRequestDTO,
  SetSignupCodeRequestDTO,
  LoginOtpSendDTO,
  InfrastructureDataDTO,
  ResetPasswordRequestDTO
} from '../../models';
import { Capacitor, CapacitorHttp, HttpOptions } from '@capacitor/core';
import { CapToNgHttp, NgToCapHttp } from '../../utils';
import { HttpReqService } from '@api/services/http-req/http-req.service';
import { BaseHttpResponse } from '@core/models';

@Injectable()
export class ApiService implements Api {
  protected baseUrl: string;

  protected apiUrl: string;

  protected securityUrl: string;

  protected ibmUrl: string;

  readonly nativeHttp = Capacitor.isNativePlatform();

  constructor(
    protected http: HttpClient,
    protected outputAdapter: OutputAdapterService,
    protected inputAdapter: InputAdapterService,
    protected httpReqService: HttpReqService
  ) {
    this.baseUrl = `${environment.baseUrl}`;
    this.apiUrl = `${environment.apiBaseUrl}`;
    this.securityUrl = `${environment.securityBaseUrl}`;
    this.ibmUrl = `${environment.ibmBaseUrl}`;
  }

  putLog(params: ApiParams<void, LogRequestDTO>): Observable<any> {
    const url = `${this.apiUrl}/v1/user/action`;

    return this.http
      .put<any>(
        url,
        this.inputAdapter.putLog(params).body,
        this.inputAdapter.putLog(params).options
      )
      .pipe(map(res => this.outputAdapter.putLog(res)));
  }

  getMobileStatus(
    params: ApiParams<void, void>
  ): Observable<InfrastructureDataDTO> {
    const url = `${this.baseUrl}/mobile/status`;

    return this.http
      .get<InfrastructureDataDTO>(
        url,
        this.inputAdapter.getMobileStatus(params).options
      )
      .pipe(map(res => this.outputAdapter.getMobileStatus(res)));
  }

  getLabels(params: ApiParams<void, void>): Observable<LabelListDTO> {
    const url = `${this.apiUrl}/v1/labels`;

    return this.http
      .get<LabelListDTO>(url, this.inputAdapter.getLabels(params).options)
      .pipe(map(res => this.outputAdapter.getLabels(res)));
  }

  getDomain(
    params: ApiParams<{ code: string }, void>
  ): Observable<DomainListDTO> {
    const url = `${this.apiUrl}/v1/domains/${params.pathParams.code}`;

    return this.http
      .get<DomainListDTO>(url, this.inputAdapter.getDomain(params).options)
      .pipe(map(res => this.outputAdapter.getDomain(res)));
  }

  postIBMClient(
    params: ApiParams<void, IBMClientRequestDTO>
  ): Observable<IBMClientDTO> {
    const url = `${this.ibmUrl}/clients`;

    return this.http
      .post<void>(
        url,
        this.inputAdapter.postIBMClient(params).body,
        this.inputAdapter.postIBMClient(params).options
      )
      .pipe(map(res => this.outputAdapter.postIBMClient(res)));
  }

  postSetSignupCode(
    params: ApiParams<void, SetSignupCodeRequestDTO>
  ): Observable<any> {
    const url = `${this.securityUrl}/vx1/setSignupCode`;

    return this.http
      .post<void>(
        url,
        this.inputAdapter.postSetSignupCode(params).body,
        this.inputAdapter.postSetSignupCode(params).options
      )
      .pipe(map(res => this.outputAdapter.postSetSignupCode(res)));
  }

  postRegister(params: ApiParams<void, RegisterRequestDTO>): Observable<any> {
    const url = `${this.securityUrl}/vx1/register`;

    return this.http
      .post<void>(
        url,
        this.inputAdapter.postRegister(params).body,
        this.inputAdapter.postRegister(params).options
      )
      .pipe(map(res => this.outputAdapter.postRegister(res)));
  }

  postIBMToken(
    params: ApiParams<void, IBMLoginRequestDTO>
  ): Observable<IBMLoginDTO> {
    const url = `${this.ibmUrl}/token`;

    return this.http
      .post<void>(
        url,
        this.inputAdapter.postIBMToken(params).body,
        this.inputAdapter.postIBMToken(params).options
      )
      .pipe(map(res => this.outputAdapter.postIBMToken(res)));
  }

  postLoginOtpSend(
    params: ApiParams<void, LoginOtpSendRequestDTO>
  ): Observable<LoginOtpSendDTO> {
    const url = `${this.securityUrl}/vx1/otp/generate`;

    return this.http
      .post<void>(
        url,
        this.inputAdapter.postLoginOtpSend(params).body,
        this.inputAdapter.postLoginOtpSend(params).options
      )
      .pipe(map(res => this.outputAdapter.postLoginOtpSend(res)));
  }

  postLoginOtpVerify(
    params: ApiParams<void, LoginOtpVerifyRequestDTO>
  ): Observable<any> {
    const url = `${this.securityUrl}/vx1/otp/verify`;

    return this.http
      .post<void>(
        url,
        this.inputAdapter.postLoginOtpVerify(params).body,
        this.inputAdapter.postLoginOtpVerify(params).options
      )
      .pipe(map(res => this.outputAdapter.postLoginOtpVerify(res)));
  }

  postLogout(params: ApiParams<void, any>): Observable<any> {
    const url = `${this.securityUrl}/vx1/logout`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postLogout(params).body,
        this.inputAdapter.postLogout(params).options
      )
      .pipe(map(res => this.outputAdapter.postLogout(res)));
  }

  postResetPassword(
    params: ApiParams<void, ResetPasswordRequestDTO>
  ): Observable<any> {
    const url = `${this.securityUrl}/vx1/resetPassword`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postResetPassword(params).body,
        this.inputAdapter.postResetPassword(params).options
      )
      .pipe(map(res => this.outputAdapter.postResetPassword(res)));
  }

  postSecurityLogon(params: ApiParams<void, AuthenticateDTO>): Observable<any> {
    const url = `${this.securityUrl}/authenticate`;

    return this.http
      .post<void>(
        url,
        this.inputAdapter.postSecurityLogon(params).body,
        this.inputAdapter.postSecurityLogon(params).options
      )
      .pipe(map(res => this.outputAdapter.postSecurityLogon(res)));
  }

  getSecurityVerify(params: ApiParams<void, void>): Observable<void> {
    const url = `${this.securityUrl}/verify`;

    return this.http
      .get<void>(url, this.inputAdapter.getSecurityVerify(params).options)
      .pipe(map(res => this.outputAdapter.getSecurityVerify(res)));
  }

  getSecurityUserData(params: ApiParams<void, void>): Observable<UserDataDTO> {
    const url = `${this.securityUrl}/userdata`;

    return this.http
      .get<UserDataDTO>(
        url,
        this.inputAdapter.getSecurityUserData(params).options
      )
      .pipe(map(res => this.outputAdapter.getSecurityUserData(res)));
  }

  getOnboardingCustomer(
    params: ApiParams<{ userId: string }, void>
  ): Observable<UserCustomerDTO> {
    const url = `${this.apiUrl}/v1/onboarding/customers/${params.pathParams.userId}`;

    return this.http
      .get<UserCustomerDTO>(
        url,
        this.inputAdapter.getOnboardingCustomer(params).options
      )
      .pipe(map(res => this.outputAdapter.getOnboardingCustomer(res)));
  }

  putOnboardingCustomer(
    params: ApiParams<{ userId: string }, UserCustomerUpdateDTO>
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/onboarding/customers/${params.pathParams.userId}`;

    return this.http
      .put<any>(
        url,
        this.inputAdapter.putOnboardingCustomer(params).body,
        this.inputAdapter.putOnboardingCustomer(params).options
      )
      .pipe(map(res => this.outputAdapter.putOnboardingCustomer(res)));
  }

  getQuotationsBoundaries(
    params: ApiParams<void, void>
  ): Observable<QuotationBoundariesDTO> {
    const url = `${this.apiUrl}/v1/quotations/simulator-boundaries`;

    return this.http
      .get<QuotationBoundariesDTO>(
        url,
        this.inputAdapter.getQuotationsBoundaries(params).options
      )
      .pipe(map(res => this.outputAdapter.getQuotationsBoundaries(res)));
  }

  postQuotations(params: ApiParams<void, QuotationParamsDTO>): Observable<any> {
    const url = `${this.apiUrl}/v1/quotations`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postQuotations(params).body,
        this.inputAdapter.postQuotations(params).options
      )
      .pipe(map(res => this.outputAdapter.postQuotations(res)));
  }

  postPositionSave(
    params: ApiParams<void, PositionSaveRequestDTO>
  ): Observable<string> {
    const url = `${this.apiUrl}/v1/positions/save`;

    return this.http
      .post<string>(
        url,
        this.inputAdapter.postPositionSave(params).body,
        this.inputAdapter.postPositionSave(params).options
      )
      .pipe(map(res => this.outputAdapter.postPositionSave(res)));
  }

  postPosition(
    params: ApiParams<void, PositionRequestDTO>
  ): Observable<PositionCreateDTO> {
    const url = `${this.apiUrl}/v1/position`;

    return this.http
      .post<PositionCreateDTO>(
        url,
        this.inputAdapter.postPosition(params).body,
        this.inputAdapter.postPosition(params).options
      )
      .pipe(map(res => this.outputAdapter.postPosition(res)));
  }

  putPosition(
    params: ApiParams<void, { positionId: string }>
  ): Observable<PositionCreateDTO> {
    const url = `${this.apiUrl}/v2/position`;

    return this.http
      .put<PositionCreateDTO>(
        url,
        this.inputAdapter.putPosition(params).body,
        this.inputAdapter.putPosition(params).options
      )
      .pipe(map(res => this.outputAdapter.putPosition(res)));
  }

  getPersonalPositions(
    params: ApiParams<void, void>
  ): Observable<PositionDTO[]> {
    const url = `${this.apiUrl}/v1/personal/positions`;

    return this.http
      .get<PositionDTO[]>(
        url,
        this.inputAdapter.getPersonalPositions(params).options
      )
      .pipe(map(res => this.outputAdapter.getPersonalPositions(res)));
  }

  getPersonalTasks(params: ApiParams<void, void>): Observable<TaskListDTO> {
    const url = `${this.apiUrl}/v1/personal/tasks`;

    return this.http
      .get<TaskListDTO>(url, this.inputAdapter.getPersonalTasks(params).options)
      .pipe(map(res => this.outputAdapter.getPersonalTasks(res)));
  }

  getPosition(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<PositionDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}`;

    return this.http
      .get<PositionDTO>(url, this.inputAdapter.getPosition(params).options)
      .pipe(map(res => this.outputAdapter.getPosition(res)));
  }

  getPositionQuotation(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<QuotationDTO> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/quotation`;

    return this.http
      .get<PositionDTO>(
        url,
        this.inputAdapter.getPositionQuotation(params).options
      )
      .pipe(map(res => this.outputAdapter.getPositionQuotation(res)));
  }

  getPositionCustomer(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<CustomerDTO> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/customer`;

    return this.http
      .get<PositionDTO>(
        url,
        this.inputAdapter.getPositionCustomer(params).options
      )
      .pipe(map(res => this.outputAdapter.getPositionCustomer(res)));
  }

  getPositionFull(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<PositionDTO> {
    const url = `${this.apiUrl}/v1/positions/full/${params.pathParams.positionId}`;

    return this.http
      .get<PositionDTO>(url, this.inputAdapter.getPositionFull(params).options)
      .pipe(map(res => this.outputAdapter.getPositionFull(res)));
  }

  postPositionWithdraw(
    params: ApiParams<{ positionId: string }, { reasonCode: string }>
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/withdraw`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postPositionWithdraw(params).body,
        this.inputAdapter.postPositionWithdraw(params).options
      )
      .pipe(map(res => this.outputAdapter.postPositionWithdraw(res)));
  }

  postPositionRelease(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/setup`;

    return this.http
      .post<any>(url, {}, this.inputAdapter.postPositionRelease(params).options)
      .pipe(map(res => this.outputAdapter.postPositionRelease(res)));
  }

  postPositionFinalize(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<FinalizeDTO> {
    const url = `${this.securityUrl}/v1/positions/${params.pathParams.positionId}/finalize`;

    return this.http
      .post<FinalizeDTO>(
        url,
        {},
        this.inputAdapter.postPositionFinalize(params).options
      )
      .pipe(map(res => this.outputAdapter.postPositionFinalize(res)));
  }

  getPreWizard(
    params: ApiParams<{ wizardCode: string }, void>
  ): Observable<WizardDTO> {
    const url = `${this.apiUrl}/v1/wizards/${params.pathParams.wizardCode}`;

    return this.http
      .get<WizardDTO>(url, this.inputAdapter.getPreWizard(params).options)
      .pipe(map(res => this.outputAdapter.getPreWizard(res)));
  }

  getWizard(
    params: ApiParams<{ positionId: string; wizardCode: string }, void>
  ): Observable<WizardDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/wizards/${params.pathParams.wizardCode}`;

    return this.http
      .get<WizardDTO>(url, this.inputAdapter.getWizard(params).options)
      .pipe(map(res => this.outputAdapter.getWizard(res)));
  }

  putWizardStepNext(
    params: ApiParams<
      { positionId: string; wizardCode: string; stepCode: string },
      Record<string, never>
    >
  ): Observable<WizardStepNextOutputDTO> {
    // eslint-disable-next-line max-len
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/wizards/${params.pathParams.wizardCode}/steps/${params.pathParams.stepCode}/next`;

    return this.http
      .put<WizardStepNextOutputDTO>(
        url,
        this.inputAdapter.putWizardStepNext(params).body,
        this.inputAdapter.putWizardStepNext(params).options
      )
      .pipe(map(res => this.outputAdapter.putWizardStepNext(res)));
  }

  putWizardStepOpen(
    params: ApiParams<
      {
        positionId: string;
        wizardCode: string;
        stepCode: string;
      },
      Record<string, never>
    >
  ): Observable<WizardStepNextOutputDTO> {
    // eslint-disable-next-line max-len
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/wizards/${params.pathParams.wizardCode}/steps/${params.pathParams.stepCode}/open`;

    return this.http
      .put<WizardStepNextOutputDTO>(
        url,
        this.inputAdapter.putWizardStepOpen(params).body,
        this.inputAdapter.putWizardStepOpen(params).options
      )
      .pipe(map(res => this.outputAdapter.putWizardStepOpen(res)));
  }

  putWorkflowTaskClose(
    params: ApiParams<
      { positionId: string; stepCode: string },
      Record<string, never>
    >
  ): Observable<WizardStepNextOutputDTO> {
    const url = `${this.apiUrl}/v1/workflow/tasks/close/${params.pathParams.positionId}/step/${params.pathParams.stepCode}`;

    return this.http
      .put<any>(
        url,
        this.inputAdapter.putWorkflowTaskClose(params).body,
        this.inputAdapter.putWorkflowTaskClose(params).options
      )
      .pipe(map(res => this.outputAdapter.putWorkflowTaskClose(res)));
  }

  getSurvey(
    params: ApiParams<{ positionId: string; surveyCode: string }, void>
  ): Observable<SurveyDTO[]> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/surveys/${params.pathParams.surveyCode}`;

    return this.http
      .get<SurveyDTO[]>(url, this.inputAdapter.getSurvey(params).options)
      .pipe(map(res => this.outputAdapter.getSurvey(res)));
  }

  putSurvey(
    params: ApiParams<
      { positionId: string; surveyCode: string },
      SurveyUpdateDTO
    >
  ) {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/surveys/${params.pathParams.surveyCode}`;

    return this.http
      .put<any>(
        url,
        this.inputAdapter.putSurvey(params).body,
        this.inputAdapter.putSurvey(params).options
      )
      .pipe(map(res => this.outputAdapter.putSurvey(res)));
  }

  getForm(
    params: ApiParams<{ positionId: string; formCode: string }, void>
  ): Observable<FormDTO[]> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/forms/${params.pathParams.formCode}`;

    return this.http
      .get<FormDTO[]>(url, this.inputAdapter.getForm(params).options)
      .pipe(map(res => this.outputAdapter.getForm(res)));
  }

  postForm(
    params: ApiParams<{ positionId: string; formCode: string }, FormUpdateDTO>
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/forms/${params.pathParams.formCode}`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postForm(params).body,
        this.inputAdapter.postForm(params).options
      )
      .pipe(map(res => this.outputAdapter.postForm(res)));
  }

  getChecklist(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<ChecklistDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/checklists/current`;

    return this.http
      .get<ChecklistDTO>(url, this.inputAdapter.getChecklist(params).options)
      .pipe(map(res => this.outputAdapter.getChecklist(res)));
  }

  getChecklistDocumentDownload(
    params: ApiParams<{ positionId: string; docId: string }, void>
  ): Observable<BaseHttpResponse<any>> {
    // eslint-disable-next-line max-len
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/checklists/current/documents/${params.pathParams.docId}/download`;
    const options =
      this.inputAdapter.getChecklistDocumentDownload(params).options;

    if (this.nativeHttp) {
      const capOptions: HttpOptions = {
        url,
        headers: NgToCapHttp.convertHeaders(options.headers),
        params: NgToCapHttp.convertParams(options.params),
        responseType: options.responseType
      };

      // gli header vanno aggiunti manualmente in quanto l'http interceptor di angular non passa
      this.httpReqService.setHeadersOnNativeRequest(capOptions);

      return from(CapacitorHttp.get(capOptions)).pipe(
        map(res => ({
          body: res.data,
          headers: CapToNgHttp.convertHeaders(res.headers),
          status: res.status,
          url: res.url
        })),
        map(res => this.outputAdapter.getChecklistDocumentDownload(res))
      );
    } else {
      return this.http
        .get(url, options)
        .pipe(map(res => this.outputAdapter.getChecklistDocumentDownload(res)));
    }
  }

  postChecklistDocumentUpload(
    params: ApiParams<{ positionId: string; docId: string }, FormData>
  ): Observable<number> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/checklists/current/documents/${params.pathParams.docId}/file`;

    return this.http
      .post<number>(
        url,
        this.inputAdapter.postChecklistDocumentUpload(params).body,
        this.inputAdapter.postChecklistDocumentUpload(params).options
      )
      .pipe(map(res => this.outputAdapter.postChecklistDocumentUpload(res)));
  }

  putChecklistDocumentOverride(
    params: ApiParams<
      { positionId: string; docId: string; fileId: string },
      FormData
    >
  ): Observable<number> {
    // eslint-disable-next-line max-len
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/checklists/current/documents/${params.pathParams.docId}/file/${params.pathParams.fileId}`;

    return this.http
      .put<number>(
        url,
        this.inputAdapter.putChecklistDocumentOverride(params).body,
        this.inputAdapter.putChecklistDocumentOverride(params).options
      )
      .pipe(map(res => this.outputAdapter.putChecklistDocumentOverride(res)));
  }

  getChecklistDocumentScan(
    params: ApiParams<{ positionId: string; docId: string }, void>
  ): Observable<OcrScanDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/checklists/current/documents/${params.pathParams.docId}/scan`;

    return this.http
      .get<OcrScanDTO>(
        url,
        this.inputAdapter.getChecklistDocumentScan(params).options
      )
      .pipe(map(res => this.outputAdapter.getChecklistDocumentScan(res)));
  }

  getChecklistDocumentCheckOcr(
    params: ApiParams<{ positionId: string; docId: string }, void>
  ): Observable<OcrCheckDTO> {
    // eslint-disable-next-line max-len
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/checklists/current/documents/${params.pathParams.docId}/check-ocr`;

    return this.http
      .get<OcrCheckDTO>(
        url,
        this.inputAdapter.getChecklistDocumentCheckOcr(params).options
      )
      .pipe(map(res => this.outputAdapter.getChecklistDocumentCheckOcr(res)));
  }

  getReports(
    params: ApiParams<{ printType: string }, void>
  ): Observable<ReportDTO[]> {
    const url = `${this.apiUrl}/v1/reports/${params.pathParams.printType}`;

    return this.http
      .get<ReportDTO[]>(url, this.inputAdapter.getReports(params).options)
      .pipe(map(res => this.outputAdapter.getReports(res)));
  }

  postReportModule(
    params: ApiParams<void, any>
  ): Observable<BaseHttpResponse<any>> {
    const url = `${this.apiUrl}/v1/reports/module`;
    const body = this.inputAdapter.postReportModule(params).body;
    const options = this.inputAdapter.postReportModule(params).options;

    if (this.nativeHttp) {
      const capOptions: HttpOptions = {
        url,
        data: body,
        headers: NgToCapHttp.convertHeaders(options.headers),
        params: NgToCapHttp.convertParams(options.params),
        responseType: options.responseType
      };

      // gli header vanno aggiunti manualmente in quanto l'http interceptor di angular non passa
      this.httpReqService.setHeadersOnNativeRequest(capOptions);

      return from(CapacitorHttp.post(capOptions)).pipe(
        map(res => ({
          body: res.data,
          headers: CapToNgHttp.convertHeaders(res.headers),
          status: res.status,
          url: res.url
        })),
        map(res => this.outputAdapter.postReportModule(res))
      );
    } else {
      return this.http
        .post<any>(url, body, options)
        .pipe(map(res => this.outputAdapter.postReportModule(res)));
    }
  }

  postReport(
    params: ApiParams<
      { positionId: string; reportCode: string },
      ReportRequestDTO
    >
  ): Observable<BaseHttpResponse<any>> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/reports/${params.pathParams.reportCode}`;
    const body = this.inputAdapter.postReport(params).body;
    const options = this.inputAdapter.postReport(params).options;

    if (this.nativeHttp) {
      const capOptions: HttpOptions = {
        url,
        data: body,
        headers: NgToCapHttp.convertHeaders(options.headers),
        params: NgToCapHttp.convertParams(options.params),
        responseType: options.responseType
      };

      // gli header vanno aggiunti manualmente in quanto l'http interceptor di angular non passa
      this.httpReqService.setHeadersOnNativeRequest(capOptions);

      return from(CapacitorHttp.post(capOptions)).pipe(
        map(res => ({
          body: res.data,
          headers: CapToNgHttp.convertHeaders(res.headers),
          status: res.status,
          url: res.url
        })),
        map(res => this.outputAdapter.postReport(res))
      );
    } else {
      return this.http
        .post<any>(url, body, options)
        .pipe(map(res => this.outputAdapter.postReport(res)));
    }
  }

  postIdentificationStart(
    params: ApiParams<{ method: string }, IdentificationStartRequestDTO>
  ): Observable<IdentificationStartDTO> {
    const url = `${this.apiUrl}/v1/identifications/${params.pathParams.method}/start`;

    return this.http
      .post<IdentificationStartDTO>(
        url,
        this.inputAdapter.postIdentificationStart(params).body,
        this.inputAdapter.postIdentificationStart(params).options
      )
      .pipe(map(res => this.outputAdapter.postIdentificationStart(res)));
  }

  getIdentificationVerify(
    params: ApiParams<{ method: string }, void>
  ): Observable<IdentificationVerifyDTO> {
    const url = `${this.apiUrl}/v1/identifications/${params.pathParams.method}/verify`;

    return this.http
      .get<IdentificationVerifyDTO>(
        url,
        this.inputAdapter.getIdentificationVerify(params).options
      )
      .pipe(map(res => this.outputAdapter.getIdentificationVerify(res)));
  }

  postSignStart(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<DigitalSignStartDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/sign/start`;

    return this.http
      .post<DigitalSignStartDTO>(
        url,
        {},
        this.inputAdapter.postSignStart(params).options
      )
      .pipe(map(res => this.outputAdapter.postSignStart(res)));
  }

  getSignVerify(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<DigitalSignVerifyDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/sign/verify`;

    return this.http
      .get<DigitalSignVerifyDTO>(
        url,
        this.inputAdapter.getSignVerify(params).options
      )
      .pipe(map(res => this.outputAdapter.getSignVerify(res)));
  }

  getSignResume(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<DigitalSignStartDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/sign/resume`;

    return this.http
      .get<DigitalSignStartDTO>(
        url,
        this.inputAdapter.getSignResume(params).options
      )
      .pipe(map(res => this.outputAdapter.getSignResume(res)));
  }

  postPsd2Start(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<BankAccountLinkDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/psd2/start`;

    return this.http
      .post<BankAccountLinkDTO>(
        url,
        this.inputAdapter.postPsd2Start(params).body,
        this.inputAdapter.postPsd2Start(params).options
      )
      .pipe(map(res => this.outputAdapter.postPsd2Start(res)));
  }

  getPsd2Verify(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<BankAccountEvaluationDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/psd2/verify`;

    return this.http
      .get<BankAccountEvaluationDTO>(
        url,
        this.inputAdapter.getPsd2Verify(params).options
      )
      .pipe(map(res => this.outputAdapter.getPsd2Verify(res)));
  }

  getPositionQuotationBoundaries(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<QuotationBoundariesDTO> {
    const url = `${this.apiUrl}/v1/quotations/${params.pathParams.positionId}/boundaries`;

    return this.http
      .get<QuotationBoundariesDTO>(
        url,
        this.inputAdapter.getPositionQuotationBoundaries(params).options
      )
      .pipe(map(res => this.outputAdapter.getPositionQuotationBoundaries(res)));
  }

  putPositionQuotation(
    params: ApiParams<{ positionId: string }, QuotationParamsDTO>
  ): Observable<void> {
    const url = `${this.apiUrl}/v1/quotations/${params.pathParams.positionId}`;

    return this.http
      .put<void>(
        url,
        this.inputAdapter.putPositionQuotation(params).body,
        this.inputAdapter.putPositionQuotation(params).options
      )
      .pipe(map(res => this.outputAdapter.putPositionQuotation(res)));
  }

  putObligationConfirm(
    params: ApiParams<
      { positionId: string; obligationId: string },
      { isConfirmed: boolean }
    >
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/obligation/${params.pathParams.positionId}/confirm/${params.pathParams.obligationId}`;

    return this.http
      .put<any>(
        url,
        this.inputAdapter.putObligationConfirm(params).body,
        this.inputAdapter.putObligationConfirm(params).options
      )
      .pipe(map(res => this.outputAdapter.putObligationConfirm(res)));
  }

  getNotes(
    params: ApiParams<{ positionId: string; type: string }, void>
  ): Observable<NoteDTO[]> {
    const url = `${this.apiUrl}/v1/note/${params.pathParams.positionId}/${params.pathParams.type}`;

    return this.http
      .get<NoteDTO[]>(url, this.inputAdapter.getNotes(params).options)
      .pipe(map(res => this.outputAdapter.getNotes(res)));
  }

  getAtc(params: ApiParams<void, void>): Observable<AtcDTO[]> {
    const url = `${this.apiUrl}/v1/atc`;

    return this.http
      .get<AtcDTO[]>(url, this.inputAdapter.getAtc(params).options)
      .pipe(map(res => this.outputAdapter.getAtc(res)));
  }

  postAtc(
    params: ApiParams<{ atcId: number; positionId: string }, AtcDTO>
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/atc/${params.pathParams.atcId}/${params.pathParams.positionId}`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postAtc(params).body,
        this.inputAdapter.postAtc(params).options
      )
      .pipe(map(res => this.outputAdapter.postAtc(res)));
  }

  postContactSendOtp(
    params: ApiParams<
      { positionId: string; channelId: string },
      { contact: string }
    >
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/contacts/positions/${params.pathParams.positionId}/channel/${params.pathParams.channelId}`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postContactSendOtp(params).body,
        this.inputAdapter.postContactSendOtp(params).options
      )
      .pipe(map(res => this.outputAdapter.postContactSendOtp(res)));
  }

  putContactVerifyOtp(
    params: ApiParams<
      { positionId: string; channelId: string },
      { contact: string; otp: string }
    >
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/contacts/positions/${params.pathParams.positionId}/channel/${params.pathParams.channelId}`;

    return this.http
      .put<any>(
        url,
        this.inputAdapter.putContactVerifyOtp(params).body,
        this.inputAdapter.putContactVerifyOtp(params).options
      )
      .pipe(map(res => this.outputAdapter.putContactVerifyOtp(res)));
  }

  postContactVerify(
    params: ApiParams<
      { positionId: string; channelId: string },
      { contact: string; prefix: string }
    >
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/contacts/${params.pathParams.channelId}/verify`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postContactVerify(params).body,
        this.inputAdapter.postContactVerify(params).options
      )
      .pipe(map(res => this.outputAdapter.postContactVerify(res)));
  }

  getNotifications(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<NotificationDTO[]> {
    const url = `${this.apiUrl}/v1/notifications/${params.pathParams.positionId}`;

    return this.http
      .get<NotificationDTO[]>(
        url,
        this.inputAdapter.getNotifications(params).options
      )
      .pipe(map(res => this.outputAdapter.getNotifications(res)));
  }

  putNotification(
    params: ApiParams<
      { notificationId: string; outcome: string },
      NotificationDTO
    >
  ): Observable<void> {
    const url = `${this.apiUrl}/v1/notifications/${params.pathParams.notificationId}/${params.pathParams.outcome}`;

    return this.http
      .put<void>(
        url,
        this.inputAdapter.putNotification(params).body,
        this.inputAdapter.putNotification(params).options
      )
      .pipe(map(res => this.outputAdapter.putNotification(res)));
  }

  getQuotationCreditRequest(
    params: ApiParams<{ mode: string }, void>
  ): Observable<CreditRequestDTO> {
    const url = `${this.apiUrl}/v1/quotations/calculate/${params.pathParams.mode}/creditrequest`;

    return this.http
      .get<CreditRequestDTO>(
        url,
        this.inputAdapter.getQuotationCreditRequest(params).options
      )
      .pipe(map(res => this.outputAdapter.getQuotationCreditRequest(res)));
  }

  getQuotationCreditUse(
    params: ApiParams<{ mode: string }, void>
  ): Observable<CreditUseListDTO> {
    const url = `${this.apiUrl}/v1/quotations/calculate/${params.pathParams.mode}/credituse`;

    return this.http
      .get<CreditUseListDTO>(
        url,
        this.inputAdapter.getQuotationCreditUse(params).options
      )
      .pipe(map(res => this.outputAdapter.getQuotationCreditUse(res)));
  }

  postPostselling(
    params: ApiParams<
      { positionId: string; taskCode: string },
      PostsellingCreateDTO
    >
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/postselling/${params.pathParams.positionId}/${params.pathParams.taskCode}`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postPostselling(params).body,
        this.inputAdapter.postPostselling(params).options
      )
      .pipe(map(res => this.outputAdapter.postPostselling(res)));
  }

  postHandover(
    params: ApiParams<{ positionId: string; handoverCod: string }, void>
  ): Observable<HandoverStartDTO> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/handover/${params.pathParams.handoverCod}`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postHandover(params).body,
        this.inputAdapter.postHandover(params).options
      )
      .pipe(map(res => this.outputAdapter.postHandover(res)));
  }

  getHandover(
    params: ApiParams<{ positionId: string; handoverCod: string }, void>
  ): Observable<HandoverStatusDTO> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/handover/${params.pathParams.handoverCod}`;

    return this.http
      .get<any>(url, this.inputAdapter.getHandover(params).options)
      .pipe(map(res => this.outputAdapter.getHandover(res)));
  }

  getChannel(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<ChannelDTO> {
    const url = `${this.apiUrl}/v1/chains/${params.pathParams.positionId}`;

    return this.http
      .get<ChannelDTO>(url, this.inputAdapter.getChannel(params).options)
      .pipe(map(res => this.outputAdapter.getChannel(res)));
  }

  getCard(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<CardDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/card`;

    return this.http
      .get<CardDTO>(url, this.inputAdapter.getCard(params).options)
      .pipe(map(res => this.outputAdapter.getCard(res)));
  }

  getResumePositionInspect(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<ResumeInspectDTO> {
    const url = `${this.apiUrl}/v1/resume/${params.pathParams.positionId}/inspect`;

    return this.http
      .get<ResumeInspectDTO>(
        url,
        this.inputAdapter.getResumePositionInspect(params).options
      )
      .pipe(map(res => this.outputAdapter.getResumePositionInspect(res)));
  }

  postResumeContactsCheck(
    params: ApiParams<{ positionId: string }, { channel: string }>
  ): Observable<ResumeContactsCheckDTO> {
    const url = `${this.apiUrl}/v1/resume/${params.pathParams.positionId}/contacts/check`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postResumeContactsCheck(params).body,
        this.inputAdapter.postResumeContactsCheck(params).options
      )
      .pipe(map(res => this.outputAdapter.postResumeContactsCheck(res)));
  }

  putResumeContactsCheck(
    params: ApiParams<{ positionId: string }, { otp: string; channel: string }>
  ): Observable<any> {
    const url = `${this.securityUrl}/v1/resume/${params.pathParams.positionId}/contacts/check`;

    return this.http
      .put<any>(
        url,
        this.inputAdapter.putResumeContactsCheck(params).body,
        this.inputAdapter.putResumeContactsCheck(params).options
      )
      .pipe(map(res => this.outputAdapter.putResumeContactsCheck(res)));
  }
}
